/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import { SITE_NAME } from 'constants';
import routes from 'constants/routes';
import FooterV2 from 'pages/HomeV2/components/FooterV2';
import Nav from 'pages/HomeV2/components/Nav';
import { LOGO_URL } from 'pages/HomeV2/seo';
import {
  CALENDLY_URL,
  Container,
  Eyebrow,
  Grain,
  Lead,
  SectionHead,
  Title,
  v2,
  V2GlobalStyle,
} from 'pages/HomeV2/v2';
import { initCalendlyInline } from 'utils/calendly';
import { isPrerendering } from 'utils/prerender';

import {
  DEMO_DESCRIPTION,
  DEMO_TITLE,
  DEMO_URL,
  demoStructuredData,
} from './seo';

const Main = styled.main`
  background: ${v2.bg};
  color: ${v2.text};
  overflow-x: clip;
`;

const Wrapper = styled.section`
  position: relative;
  padding: clamp(120px, 14vw, 170px) 0 clamp(64px, 8vw, 110px);
`;

const CalendarCard = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  border-radius: 24px;
  border: 1px solid ${v2.border};
  background: #fff;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
`;

/* Calendly injects its iframe here; React never renders children into it. */
const Calendar = styled.div`
  min-width: 320px;
  height: 700px;

  @media (max-width: 640px) {
    height: 760px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-family: ${v2.fontBody};
  font-size: 16px;
  line-height: 1.6;
  color: #475569;

  a {
    color: #0073d4;
    font-weight: 700;
  }
`;

const DirectLink = styled.p`
  margin: 20px 0 0;
  text-align: center;
  font-family: ${v2.fontBody};
  font-size: 14px;
  color: ${v2.textFaint};

  a {
    color: ${v2.textMuted};
    text-decoration: underline;
  }
`;

/**
 * /demo – booking page with the Calendly calendar embedded inline. Target for
 * the "Kostenlose Demo" sitelink in Google Ads: the visitor stays on our
 * domain, so a completed booking can be attributed to the ad click (the
 * conversion itself is sent by the global listener in app/App.jsx).
 */
const Demo = () => {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    if (isPrerendering() || !calendarRef.current) return undefined;

    let active = true;
    initCalendlyInline(CALENDLY_URL, calendarRef.current)
      .then(() => active && setStatus('ready'))
      .catch(() => active && setStatus('error'));

    return () => {
      active = false;
    };
  }, []);

  // The logo in the header ("top") leads back to the start page.
  const handleNavigate = (target) => {
    if (target !== 'top') return;
    window.scrollTo(0, 0);
    navigate(routes.home);
  };

  return (
    <>
      <Helmet>
        <title>{DEMO_TITLE}</title>
        <meta name="description" content={DEMO_DESCRIPTION} />
        <link rel="canonical" href={DEMO_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:url" content={DEMO_URL} />
        <meta property="og:title" content={DEMO_TITLE} />
        <meta property="og:description" content={DEMO_DESCRIPTION} />
        <meta property="og:image" content={LOGO_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={DEMO_TITLE} />
        <meta name="twitter:description" content={DEMO_DESCRIPTION} />
        <script type="application/ld+json">
          {JSON.stringify(demoStructuredData)}
        </script>
      </Helmet>
      <V2GlobalStyle />
      <Grain aria-hidden />
      <Nav onNavigate={handleNavigate} links={[]} />
      <Main>
        <Wrapper>
          <Container>
            <SectionHead>
              <Eyebrow>Kostenlose Demo</Eyebrow>
              <Title as="h1">
                FOXI in 30 Minuten <span className="grad">live erleben</span>
              </Title>
              <Lead>
                Such Dir einen passenden Termin aus. Wir zeigen Dir, wie FOXI
                Dokumentation und Abrechnung in Deiner Praxis automatisiert, und
                beantworten Deine Fragen – kostenlos und unverbindlich.
              </Lead>
            </SectionHead>

            <CalendarCard>
              <Calendar ref={calendarRef} />
              {status !== 'ready' && (
                <Overlay>
                  {status === 'error' ? (
                    <span>
                      Der Kalender konnte nicht geladen werden.{' '}
                      <a
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-calendly-direct
                      >
                        Termin direkt bei Calendly buchen
                      </a>
                    </span>
                  ) : (
                    <span>Kalender wird geladen …</span>
                  )}
                </Overlay>
              )}
            </CalendarCard>

            <DirectLink>
              Kalender wird nicht angezeigt?{' '}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                data-calendly-direct
              >
                Termin direkt bei Calendly buchen
              </a>
            </DirectLink>
          </Container>
        </Wrapper>
      </Main>
      <FooterV2 />
    </>
  );
};

export default Demo;
